import React from 'react';
import { get, head, isArray } from 'lodash';
import { withRouter } from 'react-router-dom';
import searchQuery from 'query-string';
import * as errorOverlay from 'utils/errorOverlay';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { REDUCER } from 'constants/entity-names';
import { toDoListWidget as ToDoListWidgetView } from 'ui/views/widgets/toDoListWidget';
import { SERVICE_RESPONSE_STATUS, WIDGET_ERROR_TEMPLATE, SIZES } from 'constants/common';
import { ToDoListWidgetService } from 'services/toDoListWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import queryString from 'query-string';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { initCallState } from 'store/actions/callState';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ToDoListWidget } from 'ui/containers/ToDoListWidget';
 *
 * // render
 * <ToDoListWidget propName='propValue'/>
 */
export class _ToDoListWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );

        // create service instance
        this.toDoListWidgetService = new ToDoListWidgetService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'ToDoListWidget.fetchComponentData()' );

        // cancel ongoing request
        this.toDoListWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.toDoListWidgetService.fetchComponentData(  {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    async initiateOutboundCall( { url, payload } ) {
        log.debug( 'ToDoListWidget.initiateOutboundCall()' );

        // cancel ongoing request
        this.toDoListWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.toDoListWidgetService.initiateOutboundCall( {
            url: url,
            data: {
                data: payload
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // this.setState( {
            //     isFetching: false,
            //     networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,

            // }, () => {

                this.props.initCallState( data ); // save call data in the store
                
                // change to authorization flow route, 2nd step
                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                    search: searchQuery.stringify( {
                        step: 2,
                    } ),
                } );
                
            // } );
        } else {

            if( WIDGET_ERROR_TEMPLATE.TO_DO_OUTBOUND_CALL_FAILED === get( data, 'template', undefined ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open(); // show error

            }
        }
    }

    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {} ) {
        log.debug( 'ToDosWidget.handleActionClick()' );
        if( 'column' === type && 'taskId' === value.name  ) {
            this.props.history.push( { pathname: DASHBOARD_SUB_ROUTES.TO_DO_DETAIL, search: queryString.stringify( {
                taskId: get( item, 'id', '' )
            }  ) } );
        }

         // when `initiateCall` group action is clicked, initiate outbound call
         if( 'groupAction' === type && 'initiateCall' === value.name ) {

            const memberData = get( item, 'values.memberId', '' );

            return this.initiateOutboundCall( {
                actionName: value.name,
                url: value.url,
                payload: {
                    memberId: isArray( memberData ) ? head( memberData ): memberData
                },
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.toDoListWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <ToDoListWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_ToDoListWidget.displayName = '_ToDoListWidget';

// set default props
_ToDoListWidget.defaultProps = {
    widget: {}, // widget configuration
};

// prop types
_ToDoListWidget.propTypes = {};

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),

        // updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

export const ToDoListWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _ToDoListWidget ) );
